import { Form, Formik } from 'formik';
import * as Papa from 'papaparse';
import React, { useEffect, useMemo, useState } from 'react';
import * as Yup from 'yup';
import ErrorMessageComponent from '../components/ErrorMessage';
import LoadingSpinner from '../components/LoadingSpinner';
import MobileModal from '../components/MobileModal';
import { Alert, EmptyState, ExportButton, FormActions, FormCheckbox, FormField, FormSelect, PageHeader } from '../components/ui';
import api from '../services/api';
import { isWineryManagedRole } from '../utils/authUser';
import { ExcelColumn } from '../utils/exportToExcel';

interface Role {
  id: number;
  name: string;
  description: string;
}

interface User {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  roleId: number;
  active: boolean;
  dataAccessLevel: 'all_data' | 'own_data';
  lastLogin?: Date | null;
  role?: Role;
}

interface UserFormValues {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  roleId: number;
  active: boolean;
  dataAccessLevel: 'all_data' | 'own_data';
}

// Normalize a user object from API (handles snake_case and Role association)
const normalizeUser = (u: any): User => {
  const roleObj = u.role || u.Role || undefined;
  const normalized: User = {
    id: u.id,
    firstName: u.firstName ?? u.first_name ?? '',
    lastName: u.lastName ?? u.last_name ?? '',
    email: u.email ?? '',
    roleId: u.roleId ?? u.role_id ?? (roleObj?.id ?? 0),
    active: u.active ?? true,
    dataAccessLevel: u.dataAccessLevel ?? u.data_access_level ?? 'own_data',
    lastLogin: u.lastLogin ?? u.last_login ?? null,
    role: roleObj,
  };
  return normalized;
};

const UserSchema = Yup.object().shape({
  firstName: Yup.string().required('First name is required'),
  lastName: Yup.string().required('Last name is required'),
  email: Yup.string().email('Invalid email').required('Email is required'),
  password: Yup.string().min(6, 'Password must be at least 6 characters'),
  roleId: Yup.number().required('Role is required'),
});

const Users: React.FC<{ tabVisible?: boolean }> = ({ tabVisible = true }) => {
  const [users, setUsers] = useState<User[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<any>(null);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [isImportModalOpen, setIsImportModalOpen] = useState<boolean>(false);
  // const [confirmDelete, setConfirmDelete] = useState<{ open: boolean; userId?: number }>({ open: false }); // TODO: Implement delete confirmation
  const [csvPreviewRows, setCsvPreviewRows] = useState<any[]>([]);
  const [csvValidRows, setCsvValidRows] = useState<any[]>([]);
  const [csvErrors, setCsvErrors] = useState<Array<{ index: number; email?: string; message: string }>>([]);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setLoading(true);
        const [usersResponse, rolesResponse] = await Promise.all([
          api.get('/api/users'),
          api.get('/api/roles')
        ]);
        const normalizedUsers: User[] = Array.isArray(usersResponse.data)
          ? usersResponse.data.map((u: any) => normalizeUser(u))
          : [];
        setUsers(normalizedUsers);
        setRoles(Array.isArray(rolesResponse.data) ? rolesResponse.data : []);
      } catch (err: any) {
        console.error('Error fetching users:', err);
        setError(err);
      } finally {
        setLoading(false);
      }
    };

    if (tabVisible) fetchUsers();
  }, [tabVisible]);

  const openModal = (user: User | null = null) => {
    setEditingUser(user);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingUser(null);
  };

  // Roles selectable in create/edit UI (platform_owner is not assignable here)
  const assignableRoles = useMemo(
    () => roles.filter(isWineryManagedRole),
    [roles]
  );

  // CSV Import helpers — only winery-managed roles (excludes platform_owner)
  const roleNameToId = useMemo(() => {
    const map = new Map<string, number>();
    assignableRoles.forEach(r => {
      if (r.name) map.set(String(r.name).toLowerCase(), r.id);
    });
    return map;
  }, [assignableRoles]);

  const normalizeCsvRow = (row: any) => {
    // Accept headers: firstName, lastName, email, password, roleId, roleName|role, active
    const firstName = (row.firstName ?? row.first_name ?? '').toString().trim();
    const lastName = (row.lastName ?? row.last_name ?? '').toString().trim();
    const email = (row.email ?? '').toString().trim().toLowerCase();
    const password = (row.password ?? '').toString();
    const roleIdRaw = row.roleId ?? row.role_id;
    const roleNameRaw = row.roleName ?? row.role;
    const activeRaw = row.active;

    let roleId: number | undefined = roleIdRaw ? Number(roleIdRaw) : undefined;
    if (!roleId && roleNameRaw) {
      const mapped = roleNameToId.get(String(roleNameRaw).toLowerCase());
      if (mapped) roleId = mapped;
    }

    const active = activeRaw === undefined || activeRaw === '' ? true : String(activeRaw).toLowerCase() !== 'false' && String(activeRaw) !== '0';

    return { firstName, lastName, email, password, roleId, active };
  };

  const assignableRoleIds = useMemo(
    () => new Set(assignableRoles.map((r) => r.id)),
    [assignableRoles]
  );

  const validateCsvRow = (u: any): string | null => {
    if (!u.firstName || !u.lastName || !u.email || !u.password) return 'Missing required fields';
    if (String(u.password).length < 6) return 'Password must be at least 6 characters';
    if (!u.roleId || !assignableRoleIds.has(Number(u.roleId))) return 'Invalid role';
    return null;
  };

  const handleCsvSelected = (file: File) => {
    setError(null);
    const reader = new FileReader();
    reader.onload = () => {
      const text = reader.result as string;
      Papa.parse(text, {
        header: true,
        skipEmptyLines: true,
        complete: (results: Papa.ParseResult<any>) => {
          const data = Array.isArray(results.data) ? results.data : [];
          const preview: any[] = [];
          const valid: any[] = [];
          const errs: Array<{ index: number; email?: string; message: string }> = [];
          const seen = new Set<string>();

          data.forEach((row: any, index: number) => {
            const norm = normalizeCsvRow(row);
            let message = validateCsvRow(norm);
            const email = norm.email;
            if (!message && email) {
              if (seen.has(email)) message = 'Duplicate email in file';
              else seen.add(email);
            }

            preview.push({ ...norm, __rowIndex: index, __status: message ? 'error' : 'ok', __message: message });
            if (!message) valid.push(norm);
            else errs.push({ index, email, message });
          });

          setCsvPreviewRows(preview);
          setCsvValidRows(valid);
          setCsvErrors(errs);
          setIsImportModalOpen(true);
        },
        error: () => {
          setError('Failed to parse CSV');
        }
      });
    };
    reader.onerror = () => {
      setError('Failed to read file');
    };
    reader.readAsText(file);
  };

  const closeImportModal = () => {
    setIsImportModalOpen(false);
    setCsvPreviewRows([]);
    setCsvValidRows([]);
    setCsvErrors([]);
  };

  const importValidUsers = async () => {
    try {
      if (csvValidRows.length === 0) {
        setError('No valid users to import');
        return;
      }
      const response = await api.post('/api/users/batch', { users: csvValidRows });
      const created = Array.isArray(response.data?.created) ? response.data.created : [];
      const normalizedCreated: User[] = created.map((u: any) => normalizeUser(u));
      setUsers(prev => [...prev, ...normalizedCreated]);
      setSuccessMessage(`Imported ${normalizedCreated.length} users successfully`);
      // Show server-side row-level errors if any
      if (Array.isArray(response.data?.errors) && response.data.errors.length > 0) {
        setCsvErrors(response.data.errors);
      } else {
        closeImportModal();
      }
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: any) {
      console.error('Import users error:', err);
      setError(err);
    }
  };

  // Export columns definition for ExportButton
  const exportColumns: ExcelColumn<User>[] = [
    {
      header: 'Name',
      accessor: (u) => `${u.firstName} ${u.lastName}`.trim(),
    },
    { header: 'Email', accessor: (u) => u.email },
    {
      header: 'Role',
      accessor: (u) =>
        u.role?.name ||
        (Array.isArray(roles) && roles.find((r) => r.id === u.roleId)?.name) ||
        'Unknown',
    },
    {
      header: 'Data Access',
      accessor: (u) =>
        u.dataAccessLevel === 'all_data' ? 'All Data' : 'Own Data Only',
    },
    {
      header: 'Status',
      accessor: (u) => (u.active ? 'Active' : 'Inactive'),
    },
    {
      header: 'Last Login',
      accessor: (u) =>
        u.lastLogin ? new Date(u.lastLogin).toISOString() : 'Never',
    },
  ];

  const handleSubmit = async (values: UserFormValues, { resetForm }: any) => {
    try {
      if (editingUser) {
        // Update existing user
        const response = await api.put(`/api/users/${editingUser.id}`, values);

        // Transform response data to ensure all fields are properly formatted
        const updatedUser: User = { ...editingUser, ...normalizeUser(response.data) };

        setUsers(users.map(user => (user.id === editingUser.id ? updatedUser : user)));
        setSuccessMessage('User updated successfully!');
      } else {
        // Create new user
        const response = await api.post('/api/users', values);

        // Transform response data to ensure all fields are properly formatted
        const newUser: User = normalizeUser(response.data);

        setUsers([...users, newUser]);
        setSuccessMessage('User created successfully!');
      }
      resetForm();
      closeModal();

      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccessMessage(null);
      }, 3000);
    } catch (err: any) {
      console.error('Error saving user:', err);
      setError(err);
    }
  };

  const toggleUserActive = async (userId: number, currentActive: boolean) => {
    try {
      await api.patch(`/api/users/${userId}/status`, { active: !currentActive });

      // Update the users list with the new status
      setUsers(users.map(user =>
        user.id === userId ? { ...user, active: !currentActive } : user
      ));

      setSuccessMessage(`User ${!currentActive ? 'activated' : 'deactivated'} successfully!`);

      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccessMessage(null);
      }, 3000);
    } catch (err: any) {
      console.error('Error updating user status:', err);
      setError(err);
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="User Management"
        subtitle="Manage users and their access permissions."
        variant="compact"
        actions={(
          <div className="flex gap-3">
            <ExportButton
              data={users}
              columns={exportColumns}
              fileName="users"
              sheetName="Users"
            />
            <label className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-neutral-600 hover:bg-neutral-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 cursor-pointer">
              Import CSV
              <input
                type="file"
                accept=".csv,text/csv"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) handleCsvSelected(f);
                  // reset input so same file can be re-selected
                  e.currentTarget.value = '';
                }}
              />
            </label>
            <button
              onClick={() => openModal()}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-500 hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-200"
            >
              Add User
            </button>
          </div>
        )}
      />

      {error && (
        <ErrorMessageComponent error={error} className="mt-4" />
      )}

      {successMessage && (
        <Alert
          type="success"
          message={successMessage}
          autoHide
          onClose={() => setSuccessMessage(null)}
          className="mt-4"
        />
      )}

      <div className="mt-8 flex flex-col">
        <div className="-my-2 -mx-4 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div className="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
            <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
              <table className="min-w-full divide-y divide-monday-border">
                <thead className="bg-monday-header">
                  <tr>
                    <th
                      scope="col"
                      className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-monday-text sm:pl-6"
                    >
                      Name
                    </th>
                    <th
                      scope="col"
                      className="px-3 py-3.5 text-left text-sm font-semibold text-monday-text"
                    >
                      Email
                    </th>
                    <th
                      scope="col"
                      className="px-3 py-3.5 text-left text-sm font-semibold text-monday-text"
                    >
                      Role
                    </th>
                    <th
                      scope="col"
                      className="px-3 py-3.5 text-left text-sm font-semibold text-monday-text"
                    >
                      Data Access
                    </th>
                    <th
                      scope="col"
                      className="px-3 py-3.5 text-left text-sm font-semibold text-monday-text"
                    >
                      Status
                    </th>
                    <th
                      scope="col"
                      className="px-3 py-3.5 text-left text-sm font-semibold text-monday-text"
                    >
                      Last Login
                    </th>
                    <th
                      scope="col"
                      className="relative py-3.5 pl-3 pr-4 sm:pr-6"
                    >
                      <span className="sr-only">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-monday-border-light bg-white">
                  {users.length > 0 ? (
                    users.map((user) => (
                      <tr key={user.id}>
                        <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-monday-text sm:pl-6">
                          {user.firstName} {user.lastName}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-monday-text-muted">
                          {user.email}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-monday-text-muted">
                          {user.role?.name || (Array.isArray(roles) && roles.find(r => r.id === user.roleId)?.name) || 'Unknown'}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-monday-text-muted">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${user.dataAccessLevel === 'all_data' ? 'bg-success-500 text-white' :
                            'bg-primary-500 text-white'
                            }`}>
                            {user.dataAccessLevel === 'all_data' ? 'All Data' : 'Own Data Only'}
                          </span>
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm">
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${user.active
                              ? 'bg-success-500 text-white'
                              : 'bg-error-500 text-white'
                              }`}
                          >
                            {user.active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-monday-text-muted">
                          {user.lastLogin
                            ? new Date(user.lastLogin).toLocaleString()
                            : 'Never'}
                        </td>
                        <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                          <button
                            onClick={() => openModal(user)}
                            className="text-primary-500 hover:text-primary-600 mr-4"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => toggleUserActive(user.id, user.active)}
                            className="text-primary-500 hover:text-primary-600"
                          >
                            {user.active ? 'Deactivate' : 'Activate'}
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={7}>
                        <EmptyState
                          title="No users found"
                          message='Click "Add User" to create a user.'
                          action={{
                            label: 'Add User',
                            onClick: () => openModal(),
                          }}
                        />
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* User Create/Edit Modal */}
      <MobileModal isOpen={isModalOpen} onClose={closeModal} title={editingUser ? 'Edit User' : 'Create New User'} size="full">
        <div className="mobile-form-container">
          <Formik
            initialValues={{
              firstName: editingUser?.firstName ?? '',
              lastName: editingUser?.lastName ?? '',
              email: editingUser?.email ?? '',
              password: '', // always blank for editing
              roleId:
                editingUser?.roleId ??
                (assignableRoles.length > 0 ? assignableRoles[0].id : 0),
              active: editingUser?.active ?? true,
              dataAccessLevel: editingUser?.dataAccessLevel ?? 'own_data',
            }}
            validationSchema={UserSchema}
            onSubmit={handleSubmit}
          >
            {({ isSubmitting }) => (
              <Form className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <FormField name="firstName" label="First Name" type="text" required />
                  <FormField name="lastName" label="Last Name" type="text" required />
                </div>

                <FormField name="email" label="Email" type="email" required />

                <FormField
                  name="password"
                  label={editingUser ? 'Password (leave blank to keep current)' : 'Password'}
                  type="password"
                  hint={editingUser ? undefined : 'Must be at least 6 characters'}
                />

                <FormSelect
                  name="roleId"
                  label="Role"
                  options={
                    assignableRoles.length > 0
                      ? assignableRoles.map((role) => ({
                          value: role.id,
                          label: `${role.name} - ${role.description}`,
                        }))
                      : [{ value: 0, label: 'No roles available' }]
                  }
                  required
                />

                <FormSelect
                  name="dataAccessLevel"
                  label="Data Access Level"
                  options={[
                    { value: 'own_data', label: 'Own Data Only' },
                    { value: 'all_data', label: 'All Data' },
                  ]}
                />

                <FormCheckbox name="active" label="Active" />

                <FormActions onCancel={closeModal} isSubmitting={isSubmitting} submitText="Save" />
              </Form>
            )}
          </Formik>
        </div>
      </MobileModal>

      {/* CSV Import Preview Modal */}
      <MobileModal isOpen={isImportModalOpen} onClose={closeImportModal} title="Import Users from CSV" size="full">
        <div className="space-y-4">
          <div className="text-sm text-monday-text-muted">
            Expected headers: <code>firstName,lastName,email,password,roleId</code> or <code>roleName</code>/<code>role</code>, optional <code>active</code>.
          </div>
          <div className="overflow-x-auto border rounded">
            <table className="min-w-full divide-y divide-monday-border-light">
              <thead className="bg-monday-header">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-monday-text">First Name</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-monday-text">Last Name</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-monday-text">Email</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-monday-text">Role</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-monday-text">Active</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-monday-text">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-monday-border-light bg-white">
                {csvPreviewRows.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-3 py-4 text-sm text-monday-text-muted text-center">No rows parsed</td>
                  </tr>
                ) : (
                  csvPreviewRows.map((r, idx) => (
                    <tr key={idx} className={r.__status === 'error' ? 'bg-red-50' : ''}>
                      <td className="px-3 py-2 text-sm">{r.firstName}</td>
                      <td className="px-3 py-2 text-sm">{r.lastName}</td>
                      <td className="px-3 py-2 text-sm">{r.email}</td>
                      <td className="px-3 py-2 text-sm">{(() => {
                        const role = roles.find(x => x.id === r.roleId);
                        return role ? role.name : r.roleId || '';
                      })()}</td>
                      <td className="px-3 py-2 text-sm">{r.active ? 'Yes' : 'No'}</td>
                      <td className="px-3 py-2 text-sm">
                        {r.__status === 'ok' ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-success-500 text-white">OK</span>
                        ) : (
                          <span className="text-red-600 text-xs">{r.__message}</span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {csvErrors.length > 0 && (
            <div className="text-sm text-red-600">
              {csvErrors.length} row(s) have issues and will be skipped.
            </div>
          )}

          <div className="sm:flex sm:flex-row-reverse gap-3">
            <button
              type="button"
              onClick={importValidUsers}
              disabled={csvValidRows.length === 0}
              className="mobile-btn mobile-btn-primary w-full sm:w-auto disabled:opacity-50"
            >
              {csvValidRows.length > 0 ? `Import ${csvValidRows.length} user(s)` : 'No valid rows'}
            </button>
            <button type="button" onClick={closeImportModal} className="mobile-btn mobile-btn-secondary w-full sm:w-auto">Cancel</button>
          </div>

        </div>
      </MobileModal>
    </div>
  );
};

export default Users;
